/**
 * SlugResolver — resolves a subdomain slug to a restaurant ID before rendering the app.
 *
 * Flow:
 * 1. Check sessionStorage cache for previous resolution
 * 2. Call GET /menu-builder/public/resolve/?slug=xxx
 * 3. On success: store result, call setResolvedRestaurant(), render children
 * 4. On error: show "Restaurant not found" page
 */

import { useEffect, useState } from 'react'
import { setResolvedRestaurant, getEnvironmentConfig } from '@/config/environment'

interface ResolveResult {
  restaurant_id: string
  restaurant_name: string
  slug: string
}

type ResolverState =
  | { status: 'loading' }
  | { status: 'resolved'; data: ResolveResult }
  | { status: 'error'; message: string }

const CACHE_KEY_PREFIX = 'restohub-slug-'

export function SlugResolver({
  slug,
  children,
}: {
  slug: string
  children: React.ReactNode
}) {
  const [state, setState] = useState<ResolverState>(() => {
    // Check sessionStorage cache
    try {
      const cached = sessionStorage.getItem(CACHE_KEY_PREFIX + slug)
      if (cached) {
        const data: ResolveResult = JSON.parse(cached)
        setResolvedRestaurant(data.restaurant_id, data.slug)
        return { status: 'resolved', data }
      }
    } catch { /* ignore */ }
    return { status: 'loading' }
  })

  useEffect(() => {
    if (state.status === 'resolved') return

    const apiBaseUrl = getEnvironmentConfig().apiBaseUrl
    const url = `${apiBaseUrl}/restohub-api/menu-builder/public/resolve/?slug=${encodeURIComponent(slug)}`

    fetch(url)
      .then(async (res) => {
        if (!res.ok) {
          const text = await res.text().catch(() => '')
          if (res.status === 404) {
            setState({ status: 'error', message: 'Restaurant not found' })
          } else {
            setState({ status: 'error', message: text || `Error ${res.status}` })
          }
          return
        }
        const data: ResolveResult = await res.json()
        setResolvedRestaurant(data.restaurant_id, data.slug)
        try {
          sessionStorage.setItem(CACHE_KEY_PREFIX + slug, JSON.stringify(data))
        } catch { /* quota exceeded — fine */ }
        setState({ status: 'resolved', data })
      })
      .catch((err: Error) => {
        setState({ status: 'error', message: err.message || 'Network error' })
      })
  }, [slug, state.status])

  if (state.status === 'loading') {
    return <LoadingScreen slug={slug} />
  }

  if (state.status === 'error') {
    return <ErrorScreen slug={slug} message={state.message} />
  }

  return <>{children}</>
}

function LoadingScreen({ slug }: { slug: string }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center space-y-4">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mx-auto" />
        <p className="text-muted-foreground text-sm">
          Loading {slug}.restohub.io...
        </p>
      </div>
    </div>
  )
}

function ErrorScreen({ slug, message }: { slug: string; message: string }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center space-y-4 max-w-md px-6">
        <div className="text-5xl">🍽️</div>
        <h1 className="text-2xl font-bold text-foreground">Restaurant Not Found</h1>
        <p className="text-muted-foreground">
          We couldn't find a restaurant at <strong>{slug}.restohub.io</strong>
        </p>
        <p className="text-sm text-muted-foreground">{message}</p>
        <a
          href="https://restohub.io"
          className="inline-block mt-4 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
        >
          Go to RestoHub
        </a>
      </div>
    </div>
  )
}
